import Page from '../../../../components/PageWrapper';
import { useState } from 'react';
import GTable from '../../../../components/elements/GTable';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import useUserAccount from '../../../../hooks/userAccountHook';
import { ITableAction, ITablePaginationData } from '../../../../components/elements/GTable/GTable';
import useModal from '../../../../hooks/modalHook';
import { capitalizeSentence, sortArrayByAlphabets } from '../../../../utils/miscFunctions';
import { useQuery } from '@tanstack/react-query';
import { getAllLoans } from '../../../../services/financing';
import Loan from '../../../../models/Loan';
import useLoans from '../../../../hooks/loansHook';
import { Banknote, CheckCircle, TicketCheck, Trash, XCircleIcon } from 'lucide-react';
import React from 'react';
import Button from '../../../../components/form-control/Button';
import useRoute from '../../../../hooks/routesHook';
interface ILoansProps {
    data?: any;
}

const Loans = ({ data }: ILoansProps): JSX.Element => {
    const page = 'loan';
    const { user } = useUserAccount();
    const navigate = useNavigate();
    const { getDashboardBase } = useRoute();
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedLoans, setSelectedLoans] = useState<string[]>([]);
    const [searchString, setSearchString] = useState<string>("")
    const [selectedLoansIndexes, setSelectedLoansIndexes] = useState<number[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 0);
    const [loading, setLoading] = useState<boolean>(true);
    const [showPerPage, setShowPerPage] = useState<number>(100);
    const { ModalComponent, updateModalStates, closeModal } = useModal();
    const invalidateQueries = useQueryClient();


    const getTableRowActions = (loan: Loan) => {
        const tableActions: ITableAction[] = [];

        !loan.approved && tableActions.push(
            {
                label: 'Approve',
                icon: <CheckCircle className="h-4 w-4" />,
                callback: ()=> approveLoanModal(Number(loan.id))
            },
            {
                label: 'Reject',
                icon: <XCircleIcon className="h-4 w-4" />,
                callback: () => handleRejectLoan(Number(loan.id))
            },
            {
                label: 'Mark as paid',
                icon: <TicketCheck className="h-4 w-4" />,
                callback: () => markALoanAsPaid(Number(loan.id))
            },
            {
                label: 'Delete',
                icon: <Trash className="h-4 w-4" />,
                callback: () => removeLoanById(Number(loan.id))
            }
        )

        return sortArrayByAlphabets(tableActions);
    }

     const { columns, removeLoanById,approveLoanModal, LoanModalComponent, handleRejectLoan, markALoanAsPaid } = useLoans({ tableRowActions: getTableRowActions, });

    const fetchAllLoans = async () => {
        // if (data) {
        //     setLoading(false);
        //     return;
        // }
        try {
            const response: any = await getAllLoans(currentPage, showPerPage, 0, false, searchString);

            return response;
        } catch (error) {
        } finally {
            setLoading(false);
        }


    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        setSearchParams({ page: String(page) });
    };

    const { data: loansData, isLoading } = useQuery({
        queryKey: ['fetchAllLoans', currentPage, showPerPage, searchString],
        queryFn: () => fetchAllLoans(),
        staleTime: 1000 * 10,
        placeholderData: (prev) => prev,
        gcTime: 1000 * 60 * 30 * 5,
        select: (response) => ({
            allLoans: response?.content,
            pagination: {
            per_page: response?.size,
            total: response?.totalElements,
            totalPages: response?.totalPages,
            from: response?.pageable?.offset + 1,
            to:
                response?.totalElements >
                response?.pageable?.offset + showPerPage
                ? response?.pageable?.offset + showPerPage
                : response?.totalElements,
            },
        }),
    });
    
      const pagination = loansData?.pagination;
      const loans = React.useMemo(() => {
          return loansData?.allLoans.map((_loan: any) => new Loan(_loan));
      }, [loansData?.allLoans]);
    
    // APPLY LOAN action — Internal admin entry point into the premium-
    // financing application flow. Kept in sync with the same gate as the
    // /premium-financing "New Application" button (see
    // premiumFinancing/index.tsx) so an operator who can start a PF
    // application from either surface always sees the same audience.
    // Restricted to Internal OWNER + ADMINISTRATOR — matches the Loans
    // sidebar / route gate shipped in v1.6.3.
    const canApplyLoan = user?.isInternalAdmin()
      && (user?.isOwner() || user?.isAdministrator());

    return (
        <Page title={`${capitalizeSentence(page)}`}>
            <div className="bg-white dark:bg-darkBg w-full p-5 text-sm min-h-full rounded-lg shadow-md dark:shadow-darkBg-shadow">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div>
                        <div className="inline-flex items-center gap-2 mb-2 dark:text-darkBg-mainText">
                            <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
                            <h2 className="font-semibold text-3xl">{capitalizeSentence(page)}s</h2>
                        </div>

                        <p className="text-sm text-slate-400">
                            View a list of all {page}s and their details{' '}
                        </p>
                    </div>
                    {canApplyLoan && (
                        <Button
                            bgColor="primary"
                            label={'Apply Loan'}
                            icon={<Banknote size={20} className="mr-2" />}
                            onClick={() => navigate(`${getDashboardBase()}/premium-financing/application-creation`)}
                        />
                    )}
                </div>

                <div className="overflow-x-auto mt-4">
                    <GTable
                        selectionChanged={(selectedObjects: Loan[], selectedIndexes: number[]) => {
                            const loanIdList = selectedObjects.map((loan) => loan.id);
                            setSelectedLoans(loanIdList as string[]);
                            setSelectedLoansIndexes(selectedIndexes);
                        }}
                        columns={columns}
                        loading={isLoading}
                        data={loans ?? []}
                        classes="h-fit"
                        showSearch={true}
                        onSearch={(searchString: string) => {
                            setSearchString(searchString)
                        }}
                        tableType={'loan(s)'}
                        paginationData={pagination}
                        onPageChange={handlePageChange}
                        showPerPage={showPerPage}
                        showCheckboxes
                        stickyHeader
                        onShowPerPageChange={(page: number) => setShowPerPage(page)}
                    />
                </div>
            </div>
            <ModalComponent />
            <LoanModalComponent />
        </Page>
    );
};

export default Loans;
